import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import api from '../../utils/api';
import { PartnerEndpoints } from '../../utils/endpoints';
import AuthLayout from './AuthLayout';
import { Alert, Button, FormField } from '../ui';

// The request interceptor only carries the session token; 2FA runs on the temporary one.
const tempTokenHeader = (tempToken) => ({ headers: { Authorization: `Bearer ${tempToken}` } });

const TwoFactorSetup = () => {
  const [step, setStep] = useState('loading');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { tempToken, isFirstLogin, setAuthData, setTempToken, authData } = useAuth();
  const navigate = useNavigate();
  const setupCalled = useRef(false);
  const hasSession = Boolean(tempToken || authData?.token);

  useEffect(() => {
    if (!hasSession || setupCalled.current) return;
    setupCalled.current = true;

    const initiateSetup = async () => {
      try {
        const response = await api.post(PartnerEndpoints.twoFactorSetup, {}, tempTokenHeader(tempToken));
        setQrCodeUrl(response.data.data.qr_code_url);
        setStep('scan');
      } catch (err) {
        if (err.response?.status === 403) {
          navigate('/');
        } else {
          setError('Failed to initiate 2FA setup. Please try logging in again.');
          setStep('error');
        }
      }
    };

    initiateSetup();
  }, [hasSession, tempToken, navigate]);

  const handleActivate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post(PartnerEndpoints.twoFactorActivate, { code }, tempTokenHeader(tempToken));

      const verifyResponse = await api.post(
        PartnerEndpoints.twoFactorVerify,
        { code },
        tempTokenHeader(tempToken)
      );

      const { token, partner } = verifyResponse.data.data;
      const verifiedAuthData = { username: partner.name ?? partner.enterprise?.name, token };

      setAuthData(verifiedAuthData);
      localStorage.setItem('authData', JSON.stringify(verifiedAuthData));
      setTempToken(null);

      navigate(isFirstLogin ? '/first-login' : '/layout');
    } catch (err) {
      if (err.response?.status === 422) {
        const msg = err.response.data?.errors?.code?.[0];
        setError(msg || 'Invalid code. Please try again.');
      } else if (err.response?.status === 403) {
        setError('Session expired. Please log in again.');
        navigate('/');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!hasSession) return <Navigate to="/" replace />;
  if (!tempToken) return null;

  return (
    <AuthLayout
      title="Set up two-factor authentication"
      subtitle={
        step === 'scan'
          ? 'Scan this QR code with your authenticator app, then enter the 6-digit code it shows.'
          : undefined
      }
    >
      {step === 'loading' && (
        <div className="flex items-center justify-center gap-2 py-8 text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          <span>Generating QR code...</span>
        </div>
      )}

      {step === 'error' && <Alert variant="danger">{error}</Alert>}

      {step === 'scan' && (
        <form onSubmit={handleActivate}>
          <div className="flex justify-center mb-6">
            <div className="rounded border border-theme-border dark:border-themedark-border bg-white p-4">
              <img src={qrCodeUrl} alt="Two-factor authentication QR code" width={180} height={180} />
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <FormField label="Authentication code" htmlFor="code" required>
            <input
              type="text"
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="form-control text-center text-f-h5 tracking-widest"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              required
            />
          </FormField>

          <Button type="submit" size="lg" loading={loading} disabled={code.length !== 6} className="w-full">
            Activate & continue
          </Button>
        </form>
      )}
    </AuthLayout>
  );
};

export default TwoFactorSetup;
